import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getTokenFromStorage, getAppId } from "@/lib/token-utils";

function getAuthToken(): string | null {
  return getTokenFromStorage();
}

export function getGameIdFromToken(): string | null {
  return getAppId('storage');
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "https://bridge-api.galleon.so";

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        console.warn("Unauthorized: Token might be invalid or expired");
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.get<T>(url, config).then((res) => res.data);
  },

  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return apiClient.post<T>(url, data, config).then((res) => res.data);
  },

  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return apiClient.put<T>(url, data, config).then((res) => res.data);
  },

  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.delete<T>(url, config).then((res) => res.data);
  },
};

export default api;
